import React, { useContext, useMemo, useState } from "react";
import Input from "../Input/Input";
import config from "../../configuration.json";
import { TranslateContext } from "../../App";
// Speedy office locator (disabled — set SPEEDY_OFFICE_LOCATOR_ENABLED in shipping.js)
// import SpeedyOfficeLocator from "../Checkout/SpeedyOfficeLocator";
import {
  getSupportedDestinationTypes,
  // mapSpeedyOfficeToAddress,
  resolveLocalizedString,
  // shouldUseSpeedyLocator,
} from "../../functions/shipping";

/**
 * Checkout customer / shipping form.
 * @param {Object} props - Component props.
 * @param {Function} props.register - react-hook-form register.
 * @param {Object} props.errors - Form validation errors.
 * @param {Function} props.setValue - react-hook-form setValue.
 * @param {Function} props.onSubmit - Form submit handler.
 * @param {Array<Object>} props.shippingOptions - Normalized carrier options.
 * @param {Object|null} props.selectedCarrier - Currently selected carrier.
 * @param {Function} props.setSelectedCarrier - Carrier change handler.
 * @param {string} props.destinationType - home|office|box.
 * @param {Function} props.setDestinationType - Destination type change handler.
 * @param {Object|null} props.selectedOffice - Speedy office payload (if any).
 * @param {Function} props.setSelectedOffice - Office selection handler.
 */
function InputForm({
  register,
  errors,
  setValue,
  onSubmit,
  shippingOptions,
  selectedCarrier,
  setSelectedCarrier,
  destinationType,
  setDestinationType,
  setSelectedOffice,
}) {
  const language = useContext(TranslateContext).language;
  const [showInvoiceFields, setShowInvoiceFields] = useState(false);

  const supportedDestinations = useMemo(
    () => getSupportedDestinationTypes(selectedCarrier),
    [selectedCarrier]
  );

  // const useSpeedyLocator = shouldUseSpeedyLocator(selectedCarrier, destinationType);

  const addressPlaceholder =
    destinationType === "office"
      ? config.texts.officeAddress?.[language] || config.texts.address[language]
      : destinationType === "box"
        ? config.texts.boxAddress?.[language] || config.texts.address[language]
        : config.texts.homeAddress?.[language] || config.texts.address[language];

  /*
   * Speedy widget selection (re-enable with SPEEDY_OFFICE_LOCATOR_ENABLED + imports above).
   * const handleOfficeSelected = useCallback(
   *   (office) => {
   *     const mapped = mapSpeedyOfficeToAddress(office);
   *     setSelectedOffice(office);
   *     setValue("address", mapped.address, { shouldValidate: true });
   *     setValue("city", mapped.city, { shouldValidate: true });
   *     setValue("postalCode", mapped.postalCode, { shouldValidate: true });
   *   },
   *   [setSelectedOffice, setValue]
   * );
   */

  /**
   * Switches carrier and resets destination/office when needed.
   * @param {Object} option - Normalized shipping option.
   */
  const handleCarrierSelect = (option) => {
    setSelectedCarrier(option);
    setSelectedOffice(null);
    const nextDestinations = getSupportedDestinationTypes(option);
    if (!nextDestinations.includes(destinationType)) {
      setDestinationType(nextDestinations[0] || "home");
    }
    setValue("address", "");
    setValue("city", "");
    setValue("postalCode", "");
  };

  /**
   * Switches destination type and clears office-derived address fields.
   * @param {string} type - home|office|box.
   */
  const handleDestinationTypeChange = (type) => {
    setDestinationType(type);
    setSelectedOffice(null);
    setValue("address", "");
    setValue("city", "");
    setValue("postalCode", "");
  };

  const addressFields = (
    <>
      <Input
        register={register}
        errors={errors}
        name="address"
        validation={{ required: config.texts.addressValidation[language] }}
        placeholder={addressPlaceholder}
      />
      <div className="flex flex-row gap-4">
        <Input
          register={register}
          errors={errors}
          name="city"
          validation={{ required: config.texts.cityValidation[language] }}
          placeholder={config.texts.city[language]}
        />
        <Input
          register={register}
          errors={errors}
          name="postalCode"
          validation={{
            required: config.texts.postCodeValidation[language],
            pattern: {
              value: /^[0-9]{4}$/,
              message: config.texts.invalidPostCode[language],
            },
          }}
          placeholder={config.texts.postCode[language]}
        />
      </div>
    </>
  );

  return (
    <form onSubmit={onSubmit} className="checkout-user-input-form">
      <Input
        register={register}
        errors={errors}
        name="firstName"
        validation={{ required: config.texts.firstNameValidation[language] }}
        placeholder={config.texts.firstName[language]}
      />
      <Input
        register={register}
        errors={errors}
        name="lastName"
        validation={{ required: config.texts.lastNameValidation[language] }}
        placeholder={config.texts.lastName[language]}
      />
      <Input
        register={register}
        errors={errors}
        name="email"
        validation={{
          required: config.texts.emailValidation[language],
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
            message: config.texts.invalidEmail[language],
          },
        }}
        placeholder={config.texts.email[language]}
      />
      <Input
        register={register}
        errors={errors}
        name="phoneNumber"
        validation={{
          required: config.texts.phoneValidation[language],
          pattern: {
            value: /^(\+359|0)[0-9]{9}$/,
            message: config.texts.invalidPhone[language],
          },
        }}
        placeholder={config.texts.phone[language]}
      />

      <div className="flex flex-col gap-2">
        <p className="checkout-user-input-form-title">
          {config.texts.shippingCarrier?.[language] || config.texts.shippingDetails[language]}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {shippingOptions.map((option) => {
            const isSelected = selectedCarrier?.slug === option.slug;
            const label = resolveLocalizedString(option.name, language) || option.slug;
            return (
              <button
                key={option.slug}
                type="button"
                className={`w-[150px] h-16 px-3 py-2 rounded-lg border-2 flex items-center justify-center cursor-pointer bg-white ${
                  isSelected ? "border-blue-500" : "border-gray-300"
                }`}
                onClick={() => handleCarrierSelect(option)}
                aria-pressed={isSelected}
              >
                {option.logoUrl ? (
                  <img src={option.logoUrl} alt={label} className="w-auto object-contain" />
                ) : (
                  <span className="text-sm font-medium">{label}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="checkout-user-input-form-title">
          {config.texts.destinationType?.[language]}
        </p>
        <div className="flex flex-wrap gap-2">
          {supportedDestinations.map((type) => {
            const labelKey =
              type === "home"
                ? "destinationHome"
                : type === "office"
                  ? "destinationOffice"
                  : "destinationBox";
            const isSelected = destinationType === type;
            return (
              <button
                key={type}
                type="button"
                className={`px-3 py-2 rounded-lg border-2 text-sm ${
                  isSelected ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white"
                }`}
                onClick={() => handleDestinationTypeChange(type)}
                aria-pressed={isSelected}
              >
                {config.texts[labelKey]?.[language] || type}
              </button>
            );
          })}
        </div>
      </div>

      {addressFields}

      {/*
        Speedy office locator (disabled). To restore:
        1. Set SPEEDY_OFFICE_LOCATOR_ENABLED = true in src/functions/shipping.js
        2. Uncomment Speedy imports and handleOfficeSelected above
        3. Replace {addressFields} with:
        {useSpeedyLocator ? (
          <>
            <SpeedyOfficeLocator ... />
            hidden address/city/postalCode inputs + validation message
          </>
        ) : (
          addressFields
        )}
      */}

      <Input
        register={register}
        errors={errors}
        name="additionalInfo"
        placeholder={config.texts.additionalInfo[language]}
      />
      <div className="flex flex-row gap-3 items-center ">
        <input
          type="checkbox"
          id="invoiceCheckbox"
          checked={showInvoiceFields}
          onChange={() => setShowInvoiceFields(!showInvoiceFields)}
          className="size-5 lg:size-6"
        />
        <label htmlFor="invoiceCheckbox">{config.texts.wantInvoiceCheckbox[language]}</label>
      </div>
      {showInvoiceFields && (
        <>
          <p className="checkout-user-input-form-title">{config.texts.invoiceDetails[language]}</p>
          <Input
            register={register}
            errors={errors}
            name="companyName"
            validation={{ required: config.texts.companyNameValidation[language] }}
            placeholder={config.texts.companyName[language]}
          />
          <Input
            register={register}
            errors={errors}
            name="bulstat"
            validation={{ required: config.texts.bulstatValidation[language] }}
            placeholder={config.texts.bulstat[language]}
          />
          <Input
            register={register}
            errors={errors}
            name="vatRegistration"
            validation={{ required: config.texts.vatRegistrationValidation[language] }}
            placeholder={config.texts.vatRegistration[language]}
          />
          <Input
            register={register}
            errors={errors}
            name="invoiceAddress"
            validation={{ required: config.texts.invoiceAddressValidation[language] }}
            placeholder={config.texts.invoiceAddress[language]}
          />
          <Input
            register={register}
            errors={errors}
            name="mol"
            validation={{ required: config.texts.molValidation[language] }}
            placeholder={config.texts.mol[language]}
          />
        </>
      )}
    </form>
  );
}

export default InputForm;
